import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { PRODUCTS } from '../../config/appConfig';
import { IoCartOutline, IoReceiptOutline, IoAdd, IoRemove, IoClose, IoInformationCircleOutline, IoDocumentTextOutline, IoLocationOutline, IoChevronForward, IoSearchOutline, IoCheckmarkCircle, IoChevronBack, IoTrashOutline, IoCallOutline } from 'react-icons/io5';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { doc, setDoc, getDoc, updateDoc, collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';

const COLORS = {
  primary: '#4A6741',
  active: '#E85830',
  header: '#2C3640',
  background: '#F4F3F8',
};

const STATUS_ORDER = ['requested', 'quoted', 'requoted', 'confirmed', 'preparing', 'shipped', 'completed', 'cancelled'];
const STATUS_MAP = {
  requested: { label: '주문요청', color: '#FF9800' },
  quoted: { label: '견적수신', color: '#42A5F5' },
  requoted: { label: '견적재수신', color: '#FB8C00' },
  confirmed: { label: '입금확인중', color: '#26A69A' },
  preparing: { label: '배송준비중', color: '#7B1FA2' },
  shipped: { label: '배송중', color: '#5C6BC0' },
  completed: { label: '배송완료', color: '#66BB6A' },
  cancelled: { label: '취소', color: '#EF5350' },
};

const COURIER_TRACK_URL = {
  'CJ대한통운': (no) => `https://trace.cjlogistics.com/web/detail.jsp?slipno=${no}`,
  '한진택배': (no) => `https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mession_open=Y&wblnumText2=${no}`,
  '로젠택배': (no) => `https://www.ilogen.com/web/personal/trace/${no}`,
};


const DELIVERY_METHODS = [
  { value: 'parcel', label: '택배' },
  { value: 'quick', label: '퀵배송' },
  { value: 'pickup', label: '직접수령' },
  { value: 'freight', label: '화물배송' },
];

const EMPTY_ADDR_FORM = { label: '', name: '', phone: '', address: '', addressDetail: '', isDefault: false, deliveryMethod: 'parcel', deliveryMessage: '' };

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, updateQty, updateOptionQty, removeFromCart } = useCart();
  const { currentUser, userData } = useAuth();
  const [tab, setTab] = useState(searchParams.get('tab') || 'orders');
  const initStatus = searchParams.get('status') || 'requested';

  React.useEffect(() => {
    setTab(searchParams.get('tab') || 'orders');
  }, [searchParams]);
  const [checked, setChecked] = useState([]);
  const [statusFilter, setStatusFilter] = useState(initStatus);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showQuotation, setShowQuotation] = useState(false);
  const [orders, setOrders] = useState([]);

  // 주문요청 바텀시트
  const [showOrderSheet, setShowOrderSheet] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [addrForm, setAddrForm] = useState(EMPTY_ADDR_FORM);
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrToast, setAddrToast] = useState(null);
  const [editingAddrId, setEditingAddrId] = useState(null);
  const [showAddrSearch, setShowAddrSearch] = useState(false);
  const addrEmbedRef = useRef(null);
  const [invoiceRequested, setInvoiceRequested] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    name: '(주)조군산업', ceo: '조군', bizNo: '000-00-00000',
    address: '인천광역시 서구 가좌동 524-11', phone: '1544-5023',
  });

  // 사업자 정보 로드
  React.useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'companyInfo'));
        if (snap.exists()) setCompanyInfo(prev => ({ ...prev, ...snap.data() }));
      } catch (e) { console.error('사업자 정보 로드 실패:', e); }
    };
    load();
  }, []);

  // Firestore 실시간 구독
  React.useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'orders'),
      where('uid', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          date: data.createdAt?.toDate?.().toISOString().split('T')[0] || '-',
          status: data.status || 'requested',
          items: data.items || [],
          address: data.address || null,
          totalAmount: data.totalAmount || 0,
          memo: data.memo || '',
          quotation: data.quotation || null,
          quotedItems: data.quotedItems || null,
          quotedPrice: data.quotedPrice || null,
          bankAccount: data.bankAccount || null,
          courier: data.courier || null,
          trackingNo: data.trackingNo || null,
          cancelledBy: data.cancelledBy || null,
          cancelReason: data.cancelReason || '',
          prevQuotes: data.prevQuotes || [],
          invoiceRequested: data.invoiceRequested || false,
        };
      });
      setOrders(list);
    }, (err) => {
      console.error('주문 실시간 구독 실패:', err);
    });
    return () => unsub();
  }, [currentUser]);

  // 배송지 목록 불러오기
  const loadAddresses = async () => {
    // 로컬에 이미 배송지가 있으면 Firestore 조회 스킵
    if (addresses.length > 0) {
      if (!selectedAddrId) {
        const defaultAddr = addresses.find(a => a.isDefault) || addresses[0];
        if (defaultAddr) setSelectedAddrId(defaultAddr.id);
      }
      return;
    }
    if (!currentUser) return;
    try {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      const data = snap.data();
      const addrs = data?.addresses || [];
      setAddresses(addrs);
      const defaultAddr = addrs.find(a => a.isDefault) || addrs[0];
      if (defaultAddr) setSelectedAddrId(defaultAddr.id);
    } catch (err) {
      console.error('배송지 로드 실패:', err);
    }
  };

  // 주문요청 바텀시트 열기
  const handleOpenOrderSheet = () => {
    loadAddresses();
    setShowOrderSheet(true);
    setShowAddrForm(false);
    setAddrForm(EMPTY_ADDR_FORM);
  };

  // Daum 주소 검색
  const loadDaumPostcode = useCallback(() => {
    return new Promise((resolve) => {
      if (window.daum?.Postcode) { resolve(); return; }
      const s = document.createElement('script');
      s.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      s.onload = () => resolve();
      document.head.appendChild(s);
    });
  }, []);

  const openAddressSearch = useCallback(async () => {
    setShowAddrSearch(true);
    await loadDaumPostcode();
    setTimeout(() => {
      if (!addrEmbedRef.current || !window.daum?.Postcode) return;
      new window.daum.Postcode({
        oncomplete: (data) => {
          const addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
          setAddrForm(prev => ({ ...prev, address: addr }));
          setShowAddrSearch(false);
        },
        width: '100%',
        height: '100%',
      }).embed(addrEmbedRef.current);
    }, 100);
  }, [loadDaumPostcode]);

  // 배송지 저장 (신규 + 수정)
  const saveAddress = async () => {
    const needsAddress = addrForm.deliveryMethod !== 'pickup' && addrForm.deliveryMethod !== 'freight';
    if (!addrForm.name || !addrForm.phone || (needsAddress && !addrForm.address)) {
      const missing = [];
      if (!addrForm.name) missing.push('수령인');
      if (!addrForm.phone) missing.push('연락처');
      if (needsAddress && !addrForm.address) missing.push('주소');
      setAddrToast(missing.join(', '));
      setTimeout(() => setAddrToast(null), 2500);
      return;
    }
    if (!currentUser) return;
    setAddrSaving(true);
    try {
      let updated;
      if (editingAddrId) {
        // 수정
        updated = addresses.map(a => {
          if (a.id === editingAddrId) return { ...addrForm, id: editingAddrId };
          if (addrForm.isDefault) return { ...a, isDefault: false };
          return a;
        });
      } else {
        // 신규
        const newAddr = { ...addrForm, id: `addr_${Date.now()}` };
        if (newAddr.isDefault && addresses.length > 0) {
          updated = addresses.map(a => ({ ...a, isDefault: false }));
          updated.push(newAddr);
        } else {
          if (addresses.length === 0) newAddr.isDefault = true;
          updated = [...addresses, newAddr];
        }
        setSelectedAddrId(newAddr.id);
      }
      await setDoc(doc(db, 'users', currentUser.uid), { addresses: updated }, { merge: true });
      setAddresses(updated);
      setShowAddrForm(false);
      setAddrForm(EMPTY_ADDR_FORM);
      setEditingAddrId(null);
    } catch (err) {
      console.error('배송지 저장 실패:', err);
      const newAddrLocal = { ...addrForm, id: editingAddrId || `addr_${Date.now()}` };
      if (!editingAddrId && addresses.length === 0) newAddrLocal.isDefault = true;
      if (editingAddrId) {
        setAddresses(prev => prev.map(a => a.id === editingAddrId ? newAddrLocal : a));
      } else {
        setAddresses(prev => [...prev, newAddrLocal]);
        setSelectedAddrId(newAddrLocal.id);
      }
      setShowAddrForm(false);
      setAddrForm(EMPTY_ADDR_FORM);
      setEditingAddrId(null);
    } finally {
      setAddrSaving(false);
    }
  };

  // 배송지 삭제
  const deleteAddress = async (addrId) => {
    if (!currentUser) return;
    const addr = addresses.find(a => a.id === addrId);
    if (addr?.isDefault) return;
    const updated = addresses.filter(a => a.id !== addrId);
    try {
      await setDoc(doc(db, 'users', currentUser.uid), { addresses: updated }, { merge: true });
      setAddresses(updated);
      if (selectedAddrId === addrId) {
        const defaultAddr = updated.find(a => a.isDefault) || updated[0];
        setSelectedAddrId(defaultAddr?.id || null);
      }
    } catch (err) {
      console.error('배송지 삭제 실패:', err);
    }
  };

  // 주문요청 최종 제출
  const handleSubmitOrder = async () => {
    const selectedAddr = addresses.find(a => a.id === selectedAddrId);
    if (!selectedAddr || !currentUser) return;

    const checkedItems = cart.filter(item => checked.includes(item.id));
    const totalAmount = checkedItems.reduce((s, item) => s + (item.price || 0) * (item.qty || 1), 0);

    // 주문번호 생성: JG-YYYYMMDD-XXXXX
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
    const seq = String(now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()).padStart(5, '0');
    const orderNo = `JG-${dateStr}-${seq}`;

    const orderData = {
      uid: currentUser.uid,
      orderNo,
      companyName: userData?.companyName || userData?.name || '',
      status: 'requested',
      items: checkedItems.map(item => ({
        productId: item.id,
        name: item.name,
        image: item.image || '',
        qty: item.qty,
        price: item.price || 0,
        ...(item.optionQtys && Object.keys(item.optionQtys).length > 0 ? { optionQtys: item.optionQtys } : {}),
        ...(item.memo ? { memo: item.memo } : {}),
      })),
      totalAmount,
      address: selectedAddr,
      memo: checkedItems.map(item => item.memo).filter(Boolean).join(' / ') || '',
      invoiceRequested,
      createdAt: serverTimestamp(),
    };

    try {
      const docRef = await addDoc(collection(db, 'orders'), orderData);

      const newOrder = {
        id: docRef.id,
        orderNo,
        date: now.toISOString().split('T')[0],
        status: 'requested',
        items: orderData.items,
        address: selectedAddr,
        totalAmount,
        company: orderData.companyName,
      };
      setOrders(prev => [newOrder, ...prev]);

      checked.forEach(id => removeFromCart(id));
      setChecked([]);

      setShowOrderSheet(false);
      setTab('orders');
      setStatusFilter('requested');
    } catch (err) {
      console.error('주문 저장 실패:', err);
      alert('주문 저장에 실패했습니다.');
    }
  };

  // checked 초기화: cart 변경 시 전체선택
  React.useEffect(() => {
    setChecked(cart.map(c => c.id));
  }, [cart]);

  // 상태별 카운트
  const statusCounts = STATUS_ORDER.reduce((acc, key) => {
    acc[key] = orders.filter(o => o.status === key).length;
    return acc;
  }, {});

  const toggleCheck = (id) => {
    setChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setChecked(prev => prev.length === cart.length ? [] : cart.map(c => c.id));
  };

  return (
    <Container>

      {showAddrSearch && (
        <AddrOverlay>
          <AddrOverlayHeader>
            <AddrOverlayTitle>주소 검색</AddrOverlayTitle>
            <AddrOverlayClose onClick={() => setShowAddrSearch(false)}>닫기</AddrOverlayClose>
          </AddrOverlayHeader>
          <AddrEmbed ref={addrEmbedRef} />
        </AddrOverlay>
      )}

      {tab === 'cart' && (
        <CartHeader>
          <CartBackBtn onClick={() => navigate(-1)}><IoChevronBack size={22} /></CartBackBtn>
          <CartHeaderTitle>장바구니</CartHeaderTitle>
          <CartHeaderRight />
        </CartHeader>
      )}

      {tab === 'cart' ? (
        <ContentArea>
          {cart.length === 0 ? (
            <EmptyState>
              <IoCartOutline size={40} color="#ddd" />
              <EmptyText>장바구니가 비어있습니다</EmptyText>
            </EmptyState>
          ) : (
            <>
              <SelectAllRow>
                <CheckBox $checked={checked.length === cart.length} onClick={toggleAll} />
                <SelectAllText>전체선택 ({checked.length}/{cart.length})</SelectAllText>
              </SelectAllRow>
              {cart.map(item => (
                  <CartItem key={item.id}>
                    <CheckBox $checked={checked.includes(item.id)} onClick={() => toggleCheck(item.id)} />
                    {item.image ? (
                      <CartImg src={item.image} alt={item.name} />
                    ) : (
                      <CartThumb>{item.name.slice(0, 2)}</CartThumb>
                    )}
                    <CartInfo>
                      <CartName>{item.name}</CartName>
                      <CartMeta>{item.category}</CartMeta>
                      {item.optionQtys && Object.keys(item.optionQtys).length > 0 ? (
                        <CartOptionList>
                          {Object.entries(item.optionQtys).map(([val, q]) => (
                            <CartOptionRow key={val}>
                              <CartOptionName>{val}</CartOptionName>
                              <CartOptionControl>
                                <QtyBtn onClick={() => updateOptionQty(item.id, val, -1)}><IoRemove size={12} /></QtyBtn>
                                <QtyNum>{q}</QtyNum>
                                <QtyBtn onClick={() => updateOptionQty(item.id, val, 1)}><IoAdd size={12} /></QtyBtn>
                              </CartOptionControl>
                            </CartOptionRow>
                          ))}
                        </CartOptionList>
                      ) : (
                        <QtyControl>
                          <QtyBtn onClick={() => updateQty(item.id, Math.max(1, item.qty - 1))}><IoRemove size={14} /></QtyBtn>
                          <QtyNum>{item.qty}</QtyNum>
                          <QtyBtn onClick={() => updateQty(item.id, item.qty + 1)}><IoAdd size={14} /></QtyBtn>
                        </QtyControl>
                      )}
                      {item.memo && <CartMemo>{item.memo}</CartMemo>}
                    </CartInfo>
                    <RemoveBtn onClick={() => removeFromCart(item.id)}><IoClose size={18} /></RemoveBtn>
                  </CartItem>
                ))}
              <OrderRequestBtn disabled={checked.length === 0} onClick={handleOpenOrderSheet}>
                주문요청 보내기{checked.length > 0 ? ` (${checked.length})` : ''}
              </OrderRequestBtn>
            </>
          )}
        </ContentArea>
      ) : (
        <ContentArea>
          <StatusGrid>
            {STATUS_ORDER.map(key => {
              const st = STATUS_MAP[key];
              const count = statusCounts[key] || 0;
              const isActive = statusFilter === key;
              return (
                <StatusBtn key={key} $active={isActive} $color={st.color} onClick={() => setStatusFilter(isActive ? null : key)}>
                  <StatusBtnLabel $active={isActive}>{st.label}</StatusBtnLabel>
                  <StatusBtnCount $active={isActive} $color={st.color}>{count}</StatusBtnCount>
                </StatusBtn>
              );
            })}
          </StatusGrid>

          {orders.length === 0 && !statusFilter && (
            <EmptyState>
              <IoReceiptOutline size={40} color="#ddd" />
              <EmptyText>주문 내역이 없습니다</EmptyText>
            </EmptyState>
          )}

              {statusFilter && (() => {
                const filtered = orders.filter(o => o.status === statusFilter);
                if (filtered.length === 0) return (
                  <EmptyState>
                    <EmptyText>{STATUS_MAP[statusFilter].label} 상태의 주문이 없습니다</EmptyText>
                  </EmptyState>
                );
                return filtered.map(order => (
                  <OrderCard key={order.id} onClick={() => setSelectedOrder(order)}>
                    <OrderTop>
                      <OrderDate>{order.date}</OrderDate>
                    </OrderTop>
                    <OrderItems>
                      {order.items.map((item, i) => {
                        const p = PRODUCTS.find(pr => pr.id === item.productId);
                        const name = p?.name || item.name || '상품';
                        const image = p?.image || item.image;
                        return (
                          <OrderItemRow key={i}>
                            {image ? <OrderItemImg src={image} alt={name} /> : <OrderItemThumb>{name.slice(0, 2)}</OrderItemThumb>}
                            <OrderItemInfo>
                              <OrderItemName>{name}</OrderItemName>
                              {item.optionQtys && Object.keys(item.optionQtys).length > 0 ? (
                                <OrderItemQty>{Object.entries(item.optionQtys).map(([v, q]) => `${v} ${q}개`).join(', ')}</OrderItemQty>
                              ) : (
                                <OrderItemQty>수량 {item.qty}개</OrderItemQty>
                              )}
                              {item.memo && <OrderItemMemo>{item.memo}</OrderItemMemo>}
                            </OrderItemInfo>
                          </OrderItemRow>
                        );
                      })}
                    </OrderItems>
                    {order.quotedPrice && (
                      <OrderMeta>
                        <MetaLabel>견적금액</MetaLabel>
                        <MetaValue>{order.quotedPrice.toLocaleString()}원</MetaValue>
                      </OrderMeta>
                    )}
                    {order.account && (
                      <OrderMeta>
                        <MetaLabel>입금계좌</MetaLabel>
                        <MetaValue $small>{order.account}</MetaValue>
                      </OrderMeta>
                    )}
                    {order.courier && (
                      <OrderMeta>
                        <MetaLabel>배송정보</MetaLabel>
                        <MetaValue $small>{order.courier} {order.trackingNo}</MetaValue>
                      </OrderMeta>
                    )}
                    {order.cancelReason && (
                      <OrderMeta>
                        <MetaLabel>취소사유</MetaLabel>
                        <MetaValue $small $cancel>{order.cancelReason}</MetaValue>
                      </OrderMeta>
                    )}
                    {!['completed', 'cancelled'].includes(order.status) && (
                      <CorrectionBtn href="tel:010-6461-1777" onClick={e => e.stopPropagation()}>
                        <IoCallOutline size={15} />
                        주문정정신청
                      </CorrectionBtn>
                    )}
                  </OrderCard>
                ));
              })()}
        </ContentArea>
      )}
      {/* 주문요청 바텀시트 */}
      {selectedOrder && (
        <>
          <SheetOverlay onClick={() => setSelectedOrder(null)} />
          <BottomSheet>
            <SheetHandle />
            <SheetHeader>
              <SheetTitle>{STATUS_MAP[selectedOrder.status]?.label}</SheetTitle>
              <SheetClose onClick={() => setSelectedOrder(null)}><IoClose size={22} /></SheetClose>
            </SheetHeader>

            {selectedOrder.status === 'requested' && (
              <SheetBody>
                <InfoBar>
                  <IoInformationCircleOutline size={18} color="#42A5F5" />
                  <InfoText>관리자가 견적서 작성을 준비중에 있습니다</InfoText>
                </InfoBar>
                <SheetBtn onClick={async () => {
                  try {
                    await setDoc(doc(db, 'orders', selectedOrder.id), {
                      status: 'cancelled',
                      cancelledBy: 'user',
                      cancelReason: '주문자 요청에 의한 취소',
                    }, { merge: true });
                  } catch (err) {
                    console.error('취소 실패:', err);
                  }
                  setOrders(prev => prev.map(o => o.id === selectedOrder.id
                    ? { ...o, status: 'cancelled', cancelledBy: 'user', cancelReason: '주문자 요청에 의한 취소' }
                    : o
                  ));
                  setSelectedOrder(null);
                }}>
                  주문 취소
                </SheetBtn>
              </SheetBody>
            )}

            {selectedOrder.status === 'preparing' && (
              <SheetBody>
                <InfoBar>
                  <IoInformationCircleOutline size={18} color="#42A5F5" />
                  <InfoText>입금확인이 되어 자재 배송을 준비하고 있습니다</InfoText>
                </InfoBar>
              </SheetBody>
            )}

            {selectedOrder.status === 'confirmed' && (
              <SheetBody>
                <InfoBar>
                  <IoInformationCircleOutline size={18} color="#42A5F5" />
                  <InfoText>관리자가 무통장 입금 내역을 확인하게 되면 배송준비중 단계로 이동합니다</InfoText>
                </InfoBar>
              </SheetBody>
            )}

            {selectedOrder.status === 'requoted' && (
              <SheetBody>
                <InfoBar $orange>
                  <IoInformationCircleOutline size={18} color="#FB8C00" />
                  <InfoText>견적서가 재발행되었습니다. 새 견적서를 확인해주세요.</InfoText>
                </InfoBar>

                <QuoteViewBtn onClick={() => setShowQuotation(true)}>
                  <IoDocumentTextOutline size={18} />
                  새 견적서 보기
                </QuoteViewBtn>

                <SheetDivider />

                {(selectedOrder.quotedItems || selectedOrder.items).map((item, i) => {
                  const name = item.name || PRODUCTS.find(pr => pr.id === item.productId)?.name || '상품';
                  const unitPrice = item.unitPrice || 0;
                  const subtotal = item.subtotal || unitPrice * (item.qty || 1);
                  return (
                    <QuoteItemRow key={i}>
                      <QuoteItemLeft>
                        <QuoteItemName>{name}</QuoteItemName>
                        {item.option && <QuoteItemOption>{item.option}</QuoteItemOption>}
                      </QuoteItemLeft>
                      <QuoteItemDetail>
                        <span>{item.qty}개</span>
                        <span>{subtotal.toLocaleString()}원</span>
                      </QuoteItemDetail>
                    </QuoteItemRow>
                  );
                })}

                <SheetDivider />

                <QuoteTotalRow>
                  <QuoteTotalLabel>총 견적금액</QuoteTotalLabel>
                  <QuoteTotalValue>{selectedOrder.quotedPrice?.toLocaleString()}원</QuoteTotalValue>
                </QuoteTotalRow>

                {selectedOrder.bankAccount && (
                  <AccountBar>
                    <AccountLabel>무통장 입금계좌</AccountLabel>
                    <AccountValue>{selectedOrder.bankAccount.bank} {selectedOrder.bankAccount.accountNo} ({selectedOrder.bankAccount.holder})</AccountValue>
                  </AccountBar>
                )}

                {/* 이전 견적서 (비활성) */}
                {selectedOrder.prevQuotes?.length > 0 && (
                  <>
                    <SheetDivider />
                    <PrevQuoteNotice>
                      <IoInformationCircleOutline size={14} color="#8C9AA4" />
                      이전 견적 ({selectedOrder.prevQuotes.length}건)
                    </PrevQuoteNotice>
                    {selectedOrder.prevQuotes.map((pq, qi) => (
                      <PrevQuoteBox key={qi}>
                        <PrevQuoteLabel>이전 견적 #{qi + 1} — {pq.quotedPrice?.toLocaleString()}원</PrevQuoteLabel>
                      </PrevQuoteBox>
                    ))}
                  </>
                )}

                <SheetBtnRow>
                  <SheetBtnOutline onClick={async () => {
                    if (!window.confirm('주문을 취소하시겠습니까?')) return;
                    try {
                      await updateDoc(doc(db, 'orders', selectedOrder.id), { status: 'cancelled', cancelledBy: 'user', cancelReason: '사용자 취소', statusChangedAt: serverTimestamp() });
                      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, status: 'cancelled' } : o));
                      setSelectedOrder(null);
                    } catch (err) { console.error('주문 취소 실패:', err); }
                  }}>
                    주문 취소
                  </SheetBtnOutline>
                  <SheetBtn onClick={async () => {
                    try {
                      await updateDoc(doc(db, 'orders', selectedOrder.id), { status: 'confirmed', statusChangedAt: serverTimestamp() });
                      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, status: 'confirmed' } : o));
                      setSelectedOrder(null);
                    } catch (err) { console.error('입금 완료 실패:', err); }
                  }}>
                    입금 완료
                  </SheetBtn>
                </SheetBtnRow>
              </SheetBody>
            )}

            {(selectedOrder.status === 'quoted') && (
              <SheetBody>
                <QuoteViewBtn onClick={() => setShowQuotation(true)}>
                  <IoDocumentTextOutline size={18} />
                  견적서 보기
                </QuoteViewBtn>

                <SheetDivider />

                {(selectedOrder.quotedItems || selectedOrder.items).map((item, i) => {
                  const name = item.name || PRODUCTS.find(pr => pr.id === item.productId)?.name || '상품';
                  const unitPrice = item.unitPrice || 0;
                  const subtotal = item.subtotal || unitPrice * (item.qty || 1);
                  return (
                    <QuoteItemRow key={i}>
                      <QuoteItemLeft>
                        <QuoteItemName>{name}</QuoteItemName>
                        {item.option && <QuoteItemOption>{item.option}</QuoteItemOption>}
                      </QuoteItemLeft>
                      <QuoteItemDetail>
                        <span>{item.qty}개</span>
                        <span>{subtotal.toLocaleString()}원</span>
                      </QuoteItemDetail>
                    </QuoteItemRow>
                  );
                })}

                <SheetDivider />

                <QuoteTotalRow>
                  <QuoteTotalLabel>총 견적금액</QuoteTotalLabel>
                  <QuoteTotalValue>{selectedOrder.quotedPrice?.toLocaleString()}원</QuoteTotalValue>
                </QuoteTotalRow>

                {selectedOrder.bankAccount && (
                  <AccountBar>
                    <AccountLabel>무통장 입금계좌</AccountLabel>
                    <AccountValue>{selectedOrder.bankAccount.bank} {selectedOrder.bankAccount.accountNo} ({selectedOrder.bankAccount.holder})</AccountValue>
                  </AccountBar>
                )}

                <SheetBtnRow>
                  <SheetBtnOutline onClick={async () => {
                    if (!window.confirm('주문을 취소하시겠습니까?')) return;
                    try {
                      await updateDoc(doc(db, 'orders', selectedOrder.id), { status: 'cancelled', cancelledBy: 'user', cancelReason: '사용자 취소', statusChangedAt: serverTimestamp() });
                      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, status: 'cancelled' } : o));
                      setSelectedOrder(null);
                    } catch (err) { console.error('주문 취소 실패:', err); }
                  }}>
                    주문 취소
                  </SheetBtnOutline>
                  <SheetBtn onClick={async () => {
                    try {
                      await updateDoc(doc(db, 'orders', selectedOrder.id), { status: 'confirmed', statusChangedAt: serverTimestamp() });
                      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, status: 'confirmed' } : o));
                      setSelectedOrder(null);
                    } catch (err) { console.error('입금 완료 실패:', err); }
                  }}>
                    입금 완료
                  </SheetBtn>
                </SheetBtnRow>
              </SheetBody>
            )}

            {selectedOrder.status === 'shipped' && (
              <SheetBody>
                <TrackingInfo>
                  <TrackingRow>
                    <TrackingLabel>택배사</TrackingLabel>
                    <TrackingVal>{selectedOrder.courier}</TrackingVal>
                  </TrackingRow>
                  <TrackingRow>
                    <TrackingLabel>운송장번호</TrackingLabel>
                    <TrackingVal>{selectedOrder.trackingNo}</TrackingVal>
                  </TrackingRow>
                </TrackingInfo>

                <TrackingSteps>
                  {(selectedOrder.trackingSteps || []).map((s, i) => {
                    const isLast = i === selectedOrder.trackingSteps.length - 1;
                    return (
                      <TrackStep key={i}>
                        <TrackDotCol>
                          <TrackDot $done={s.done} />
                          {!isLast && <TrackLine $done={s.done} />}
                        </TrackDotCol>
                        <TrackContent>
                          <TrackStepName $done={s.done}>{s.step}</TrackStepName>
                          <TrackStepTime>{s.time || '-'}</TrackStepTime>
                        </TrackContent>
                      </TrackStep>
                    );
                  })}
                </TrackingSteps>

                <SheetBtn onClick={async () => {
                  try {
                    await updateDoc(doc(db, 'orders', selectedOrder.id), { status: 'completed', statusChangedAt: serverTimestamp() });
                    setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, status: 'completed' } : o));
                    setSelectedOrder(null);
                  } catch (err) { console.error('배송완료 처리 실패:', err); }
                }}>
                  배송완료 확인
                </SheetBtn>
              </SheetBody>
            )}

            {selectedOrder.status === 'completed' && (
              <SheetBody>
                <InfoBar $green>
                  <IoInformationCircleOutline size={18} color="#66BB6A" />
                  <InfoText>{selectedOrder.completedAt} 에 배송이 완료되었습니다</InfoText>
                </InfoBar>
              </SheetBody>
            )}

            {selectedOrder.status === 'cancelled' && (
              <SheetBody>
                <InfoBar $red>
                  <IoInformationCircleOutline size={18} color="#EF5350" />
                  <InfoText>
                    {selectedOrder.cancelledBy === 'admin' ? '관리자' : '주문자'}에 의해 취소된 주문입니다
                  </InfoText>
                </InfoBar>
                {selectedOrder.cancelReason && (
                  <CancelReasonBox>
                    <CancelReasonLabel>취소 사유</CancelReasonLabel>
                    <CancelReasonText>{selectedOrder.cancelReason}</CancelReasonText>
                  </CancelReasonBox>
                )}
              </SheetBody>
            )}
          </BottomSheet>
        </>
      )}

      {/* 주문요청 배송지 바텀시트 */}
      {showOrderSheet && (
        <>
          <SheetOverlay onClick={() => setShowOrderSheet(false)} />
          <OrderBottomSheet>
            <SheetHandle />
            <SheetHeader>
              <SheetTitle>{showAddrForm ? (editingAddrId ? '배송지 수정' : '배송지 추가') : '주문요청'}</SheetTitle>
              <SheetClose onClick={() => setShowOrderSheet(false)}><IoClose size={22} /></SheetClose>
            </SheetHeader>

            <OrderSheetBody>
              {!showAddrForm && (
                <>
                  {/* 선택된 상품 요약 */}
                  <AddrSection>
                    <AddrSectionTitle>
                      <IoCartOutline size={16} />
                      주문 상품 {checked.length}건
                    </AddrSectionTitle>
                    <OrderSummaryList>
                      {cart.filter(item => checked.includes(item.id)).map(item => (
                        <OrderSummaryItem key={item.id}>
                          <span>{item.name}</span>
                          <span>{item.qty}개</span>
                        </OrderSummaryItem>
                      ))}
                    </OrderSummaryList>
                  </AddrSection>

                  <SheetDivider />
                </>
              )}

              {/* 배송지 섹션 */}
              <AddrSection>
                <AddrSectionTitle>
                  <IoLocationOutline size={16} />
                  배송지
                </AddrSectionTitle>

                {!showAddrForm ? (
                  <>
                    {addresses.length === 0 ? (
                      <EmptyAddrBox>
                        <EmptyAddrText>등록된 배송지가 없습니다</EmptyAddrText>
                        <AddAddrBtn onClick={() => setShowAddrForm(true)}>
                          <IoAdd size={16} />
                          배송지 등록
                        </AddAddrBtn>
                      </EmptyAddrBox>
                    ) : (
                      <>
                        {addresses.map(addr => (
                          <AddrCard key={addr.id} $selected={selectedAddrId === addr.id} onClick={() => setSelectedAddrId(addr.id)}>
                            <AddrRadio $selected={selectedAddrId === addr.id}>
                              {selectedAddrId === addr.id && <IoCheckmarkCircle size={20} color={COLORS.primary} />}
                            </AddrRadio>
                            <AddrCardContent>
                              <AddrCardTop>
                                <AddrLabel>{addr.label}</AddrLabel>
                                {addr.isDefault && <DefaultBadge>기본</DefaultBadge>}
                              </AddrCardTop>
                              <AddrName>{addr.name} · {addr.phone}</AddrName>
                              <AddrText>{addr.address} {addr.addressDetail}</AddrText>
                              {addr.deliveryMethod && <AddrDeliveryTag>{DELIVERY_METHODS.find(m => m.value === addr.deliveryMethod)?.label || addr.deliveryMethod}</AddrDeliveryTag>}
                            </AddrCardContent>
                            <AddrBtnGroup>
                              <AddrEditBtn onClick={(e) => {
                                e.stopPropagation();
                                setEditingAddrId(addr.id);
                                setAddrForm({
                                  label: addr.label || '',
                                  name: addr.name || '',
                                  phone: addr.phone || '',
                                  address: addr.address || '',
                                  addressDetail: addr.addressDetail || '',
                                  isDefault: addr.isDefault || false,
                                  deliveryMethod: addr.deliveryMethod || 'parcel',
                                  deliveryMessage: addr.deliveryMessage || '',
                                });
                                setShowAddrForm(true);
                              }}>수정</AddrEditBtn>
                              {!addr.isDefault && (
                                <AddrDeleteBtn onClick={(e) => {
                                  e.stopPropagation();
                                  deleteAddress(addr.id);
                                }}>
                                  <IoTrashOutline size={14} />
                                </AddrDeleteBtn>
                              )}
                            </AddrBtnGroup>
                          </AddrCard>
                        ))}
                        <AddAddrBtn onClick={() => setShowAddrForm(true)}>
                          <IoAdd size={16} />
                          배송지 추가
                        </AddAddrBtn>
                      </>
                    )}
                  </>
                ) : (
                  /* 배송지 입력 폼 */
                  <AddrFormWrap>
                    <AddrFormRow>
                      <AddrFormLabel>배송지명</AddrFormLabel>
                      <AddrInput placeholder="예: 현장, 회사, 집" value={addrForm.label} onChange={e => setAddrForm(p => ({ ...p, label: e.target.value }))} />
                    </AddrFormRow>
                    <AddrFormRow>
                      <AddrFormLabel>수령인 <RequiredTag>(필수입력)</RequiredTag></AddrFormLabel>
                      <AddrInput placeholder="이름" value={addrForm.name} onChange={e => setAddrForm(p => ({ ...p, name: e.target.value }))} />
                    </AddrFormRow>
                    <AddrFormRow>
                      <AddrFormLabel>연락처 <RequiredTag>(필수입력)</RequiredTag></AddrFormLabel>
                      <AddrInput placeholder="010-0000-0000" value={addrForm.phone} onChange={e => setAddrForm(p => ({ ...p, phone: e.target.value }))} />
                    </AddrFormRow>
                    <AddrFormRow>
                      <AddrFormLabel>배송방법</AddrFormLabel>
                      <DeliveryMethodRow>
                        {DELIVERY_METHODS.map(m => (
                          <DeliveryMethodBtn
                            key={m.value}
                            $active={addrForm.deliveryMethod === m.value}
                            onClick={() => setAddrForm(p => ({ ...p, deliveryMethod: m.value }))}
                          >
                            {m.label}
                          </DeliveryMethodBtn>
                        ))}
                      </DeliveryMethodRow>
                    </AddrFormRow>
                    <AddrFormRow $disabled={addrForm.deliveryMethod === 'pickup' || addrForm.deliveryMethod === 'freight'}>
                      <AddrFormLabel>주소</AddrFormLabel>
                      <AddrSearchRow>
                        <AddrInput placeholder="주소 검색" value={addrForm.address} readOnly style={{ flex: 1, opacity: (addrForm.deliveryMethod === 'pickup' || addrForm.deliveryMethod === 'freight') ? 0.4 : 1 }} />
                        <AddrSearchBtn onClick={openAddressSearch} disabled={addrForm.deliveryMethod === 'pickup' || addrForm.deliveryMethod === 'freight'}><IoSearchOutline size={18} /></AddrSearchBtn>
                      </AddrSearchRow>
                    </AddrFormRow>
                    <AddrFormRow $disabled={addrForm.deliveryMethod === 'pickup' || addrForm.deliveryMethod === 'freight'}>
                      <AddrFormLabel>상세주소</AddrFormLabel>
                      <AddrInput placeholder="동/호수 등" value={addrForm.addressDetail} disabled={addrForm.deliveryMethod === 'pickup' || addrForm.deliveryMethod === 'freight'} onChange={e => setAddrForm(p => ({ ...p, addressDetail: e.target.value }))} style={{ opacity: (addrForm.deliveryMethod === 'pickup' || addrForm.deliveryMethod === 'freight') ? 0.4 : 1 }} />
                    </AddrFormRow>
                    <AddrFormRow>
                      <AddrFormLabel>배송 메시지</AddrFormLabel>
                      <AddrInput placeholder="예: 부재 시 경비실에 맡겨주세요" value={addrForm.deliveryMessage} onChange={e => setAddrForm(p => ({ ...p, deliveryMessage: e.target.value }))} />
                    </AddrFormRow>
                    <DefaultCheckRow onClick={() => setAddrForm(p => ({ ...p, isDefault: !p.isDefault }))}>
                      <CheckBox $checked={addrForm.isDefault} />
                      <DefaultCheckLabel>기본 배송지로 설정</DefaultCheckLabel>
                    </DefaultCheckRow>
                    <AddrFormBtnRow>
                      <SheetBtnOutline onClick={() => { setShowAddrForm(false); setAddrForm(EMPTY_ADDR_FORM); setEditingAddrId(null); }}>취소</SheetBtnOutline>
                      <SheetBtn onClick={saveAddress} disabled={addrSaving || !addrForm.name || !addrForm.phone || (!addrForm.address && addrForm.deliveryMethod !== 'pickup' && addrForm.deliveryMethod !== 'freight')}>
                        {addrSaving ? '저장 중...' : '저장'}
                      </SheetBtn>
                    </AddrFormBtnRow>
                  </AddrFormWrap>
                )}
              </AddrSection>

              {!showAddrForm && (
                <>
                  <SheetDivider />
                  <AddrSection>
                    <AddrSectionTitle>
                      <IoDocumentTextOutline size={16} />
                      계산서 발행
                    </AddrSectionTitle>
                    <InvoiceCheckRow>
                      <InvoiceOption $active={invoiceRequested} onClick={() => setInvoiceRequested(true)}>
                        <InvoiceRadio $active={invoiceRequested} />
                        발행
                      </InvoiceOption>
                      <InvoiceOption $active={!invoiceRequested} onClick={() => setInvoiceRequested(false)}>
                        <InvoiceRadio $active={!invoiceRequested} />
                        미발행
                      </InvoiceOption>
                    </InvoiceCheckRow>
                  </AddrSection>
                  <OrderSubmitBtn disabled={!selectedAddrId} onClick={handleSubmitOrder}>
                    주문요청 보내기
                  </OrderSubmitBtn>
                </>
              )}
            </OrderSheetBody>
          </OrderBottomSheet>
        </>
      )}

      {addrToast && (
        <AddrToast>
          <IoInformationCircleOutline size={18} color="#E85830" />
          <AddrToastText>
            <AddrToastLine1>필수 항목을 입력해주세요</AddrToastLine1>
            <AddrToastLine2>{addrToast}을(를) 입력해주세요</AddrToastLine2>
          </AddrToastText>
        </AddrToast>
      )}

      {/* 견적서 전체화면 */}
      {showQuotation && selectedOrder && (
        <QuotationOverlay onClick={() => setShowQuotation(false)}>
          <QuotationPage onClick={(e) => e.stopPropagation()}>
            <QuotationCloseBtn onClick={() => setShowQuotation(false)}><IoClose size={24} /></QuotationCloseBtn>
            <QuotationDoc>
              <QDocTitle>견 적 서</QDocTitle>
              <QDocCompany>{companyInfo.name}</QDocCompany>
              <QDocRecipient>수신처 : {userData?.companyName || userData?.name || ''} (귀하)</QDocRecipient>
              <QDocMeta>
                <QDocMetaRow><QDocMetaLabel>견적번호</QDocMetaLabel><QDocMetaVal>{selectedOrder.id.toUpperCase()}-Q</QDocMetaVal></QDocMetaRow>
                <QDocMetaRow><QDocMetaLabel>견적일자</QDocMetaLabel><QDocMetaVal>{selectedOrder.date}</QDocMetaVal></QDocMetaRow>
              </QDocMeta>

              <QDocTable>
                <QDocThead>
                  <tr>
                    <QDocTh $left>품목</QDocTh>
                    <QDocTh>수량</QDocTh>
                    <QDocTh>단가</QDocTh>
                  </tr>
                </QDocThead>
                <tbody>
                  {(selectedOrder.quotedItems || selectedOrder.items).map((item, i) => {
                    const name = item.name || PRODUCTS.find(pr => pr.id === item.productId)?.name || '상품';
                    const unitPrice = item.unitPrice || 0;
                    const subtotal = item.subtotal || unitPrice * (item.qty || 1);
                    return (
                      <React.Fragment key={i}>
                        <tr>
                          <QDocTd $left>
                            {name}
                            {item.option && <QDocOptionTag>{item.option}</QDocOptionTag>}
                          </QDocTd>
                          <QDocTd>{item.qty}</QDocTd>
                          <QDocTd>{unitPrice > 0 ? unitPrice.toLocaleString() : '-'}</QDocTd>
                        </tr>
                        {unitPrice > 0 && (
                          <tr>
                            <QDocSubtotalTd colSpan={3}>{item.qty} × {unitPrice.toLocaleString()} = {subtotal.toLocaleString()}원</QDocSubtotalTd>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </QDocTable>

              <QDocTotalRow>
                <QDocTotalLabel>합계금액</QDocTotalLabel>
                <QDocTotalValue>{selectedOrder.quotedPrice?.toLocaleString()}원</QDocTotalValue>
              </QDocTotalRow>
              {selectedOrder.invoiceRequested && (
                <QDocVatNote>※ 부가가치세 별도</QDocVatNote>
              )}

              <QDocFooter>
                <QDocNote>상기와 같이 견적합니다.</QDocNote>
                <QDocStampWrap>
                  <QDocStampLabel>{companyInfo.name}</QDocStampLabel>
                  <QDocStamp>
                    <StampInner>
                      <StampText>{companyInfo.ceo?.slice(0, 2) || '조군'}</StampText>
                      <StampText>{companyInfo.ceo?.length > 2 ? companyInfo.ceo.slice(2) : '산업'}</StampText>
                    </StampInner>
                  </QDocStamp>
                </QDocStampWrap>
              </QDocFooter>

              <QDocCompanyInfo>
                <QDocCompanyName>{companyInfo.name}</QDocCompanyName>
                <QDocCompanyDetail>대표: {companyInfo.ceo} | 사업자등록번호: {companyInfo.bizNo}</QDocCompanyDetail>
                <QDocCompanyDetail>{companyInfo.address}</QDocCompanyDetail>
                <QDocCompanyDetail>대표번호: {companyInfo.phone}</QDocCompanyDetail>
              </QDocCompanyInfo>
            </QuotationDoc>
          </QuotationPage>
        </QuotationOverlay>
      )}
    </Container>
  );
};

export default OrderHistoryPage;

const Container = styled.div`
  min-height: 100vh;
  background: ${COLORS.background};
  padding-bottom: 100px;
`;

const CartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #F0F0EC;
`;
const CartBackBtn = styled.button`
  display: flex;
  align-items: center;
  color: #1E2A32;
  padding: 4px;
`;
const CartHeaderTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;
const CartHeaderRight = styled.div` width: 32px; `;

const TabRow = styled.div`
  display: flex;
  margin: 12px 16px 0;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const Tab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 0;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? '700' : '400'};
  color: ${({ $active }) => $active ? COLORS.header : '#8C9AA4'};
  border-bottom: 2px solid ${({ $active }) => $active ? COLORS.header : 'transparent'};
`;

const TabBadge = styled.span`
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: ${COLORS.active};
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

const ContentArea = styled.div`
  padding: 12px 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 0;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #8C9AA4;
`;

/* 장바구니 */
const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const SelectAllText = styled.span`
  font-size: 13px;
  color: #5A6B75;
`;

const CheckBox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${({ $checked }) => $checked ? COLORS.header : '#D0D0D0'};
  background: ${({ $checked }) => $checked ? COLORS.header : 'white'};
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
  &::after {
    content: '${({ $checked }) => $checked ? '✓' : ''}';
    color: white;
    font-size: 12px;
    font-weight: 700;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const CartImg = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 10px;
  object-fit: cover;
  background: #F0F0F0;
  flex-shrink: 0;
`;

const CartThumb = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 10px;
  background: #EDF2EB;
  color: ${COLORS.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
`;

const RemoveBtn = styled.button`
  color: #CCC;
  display: flex;
  align-items: center;
  padding: 4px;
  align-self: flex-start;
  &:active { color: #EF5350; }
`;

const CartInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CartName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
`;

const CartMeta = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  margin-top: 2px;
`;

const CartOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const CartOptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F6F7F4;
  border-radius: 8px;
  padding: 6px 10px;
`;

const CartOptionName = styled.span`
  font-size: 12px;
  color: ${COLORS.primary};
  font-weight: 500;
`;

const CartOptionControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  background: #ECEEE9;
  border-radius: 8px;
  padding: 0 2px;
`;

const CartMemo = styled.p`
  font-size: 11px;
  color: #8C9AA4;
  margin-top: 4px;
  font-style: italic;
`;

const QtyControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-top: 8px;
`;

const QtyBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #E0E0E0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5A6B75;
`;

const QtyNum = styled.span`
  min-width: 32px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #1E2A32;
`;

const OrderRequestBtn = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 12px;
  border-radius: 12px;
  background: ${({ disabled }) => disabled ? '#D0D0D0' : COLORS.header};
  color: white;
  font-size: 14px;
  font-weight: 600;
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
`;

/* 주문현황 - 상태 버튼 그리드 */
const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 14px;
`;

const StatusBtn = styled.div`
  background: ${({ $active, $color }) => $active ? `${$color}12` : 'white'};
  border: 1px solid ${({ $active, $color }) => $active ? $color : '#EEEEED'};
  border-radius: 12px;
  padding: 12px 0;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: all 0.15s;
  &:active { transform: scale(0.96); }
`;

const StatusBtnLabel = styled.p`
  font-size: 11px;
  color: ${({ $active }) => $active ? '#1E2A32' : '#5A6B75'};
  font-weight: 500;
`;

const StatusBtnCount = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $active, $color }) => $active ? $color : '#1E2A32'};
  line-height: 1;
  margin-top: 5px;
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const OrderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const OrderDate = styled.p`
  font-size: 12px;
  color: #8C9AA4;
`;

const OrderItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OrderItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OrderItemImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  background: #F0F0F0;
  flex-shrink: 0;
`;

const OrderItemThumb = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #EDF2EB;
  color: ${COLORS.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const OrderItemInfo = styled.div`
  flex: 1;
`;

const OrderItemName = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #1E2A32;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const OrderItemQty = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  margin-top: 1px;
`;

const OrderItemMemo = styled.p`
  font-size: 11px;
  color: #B0B8C0;
  margin-top: 2px;
  font-style: italic;
`;

const OrderMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #F0F0EE;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  color: #8C9AA4;
  flex-shrink: 0;
`;

const MetaValue = styled.span`
  font-size: ${({ $small }) => $small ? '12px' : '14px'};
  font-weight: ${({ $small }) => $small ? '500' : '700'};
  color: ${({ $cancel }) => $cancel ? '#EF5350' : '#1E2A32'};
  text-align: right;
`;

const CorrectionBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #FAFAFA;
  color: #555;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  &:active { background: #F0F0F0; }
`;

/* 바텀시트 */
const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const SheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: 900;
  animation: ${fadeIn} 0.2s ease;
`;

const BottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 901;
  padding: 0 20px 34px;
  animation: ${slideUp} 0.25s ease;
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #DDD;
  margin: 10px auto 0;
`;

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 12px;
`;

const SheetTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;

const SheetClose = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #F0F0F0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5A6B75;
`;

const SheetBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ $green, $red, $orange }) => $red ? '#FFF5F5' : $green ? '#F0FFF0' : $orange ? '#FFF8E1' : '#F0F7FF'};
  border-radius: 10px;
  padding: 14px 16px;
`;

const InfoText = styled.p`
  font-size: 13px;
  color: #2C3640;
  line-height: 1.5;
`;

/* 배송추적 */
const TrackingInfo = styled.div`
  background: #F9F9F6;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TrackingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TrackingLabel = styled.span`
  font-size: 12px;
  color: #8C9AA4;
`;

const TrackingVal = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1E2A32;
`;

const TrackingSteps = styled.div`
  padding: 4px 0;
`;

const TrackStep = styled.div`
  display: flex;
  gap: 14px;
`;

const TrackDotCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 16px;
  flex-shrink: 0;
`;

const TrackDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $done }) => $done ? '#5C6BC0' : '#DDD'};
  border: 2px solid ${({ $done }) => $done ? '#5C6BC0' : '#DDD'};
  flex-shrink: 0;
`;

const TrackLine = styled.div`
  width: 2px;
  flex: 1;
  min-height: 28px;
  background: ${({ $done }) => $done ? '#5C6BC0' : '#E8E8E8'};
`;

const TrackContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  padding-bottom: 20px;
`;

const TrackStepName = styled.span`
  font-size: 13px;
  font-weight: ${({ $done }) => $done ? '600' : '400'};
  color: ${({ $done }) => $done ? '#1E2A32' : '#BBB'};
`;

const TrackStepTime = styled.span`
  font-size: 11px;
  color: #8C9AA4;
`;

const CancelReasonBox = styled.div`
  background: #F9F9F6;
  border-radius: 10px;
  padding: 14px 16px;
`;

const CancelReasonLabel = styled.p`
  font-size: 11px;
  color: #8C9AA4;
  margin-bottom: 4px;
`;

const CancelReasonText = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #1E2A32;
`;

const SheetBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  background: ${COLORS.header};
  color: white;
  font-size: 14px;
  font-weight: 600;
  &:active { opacity: 0.85; }
`;

const SheetBtnOutline = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  background: white;
  border: 1px solid ${COLORS.header};
  color: ${COLORS.header};
  font-size: 14px;
  font-weight: 600;
  &:active { opacity: 0.85; }
`;

const SheetBtnRow = styled.div`
  display: flex;
  gap: 10px;
`;

const SheetDivider = styled.div`
  height: 1px;
  background: #F0F0EE;
`;

const QuoteViewBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  background: #F4F3F8;
  color: ${COLORS.header};
  font-size: 14px;
  font-weight: 600;
  &:active { background: #EEEDF3; }
`;

const QuoteItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const QuoteItemLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuoteItemName = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #1E2A32;
  word-break: keep-all;
  line-height: 1.4;
`;

const QuoteItemOption = styled.span`
  display: inline-block;
  font-size: 11px;
  color: ${COLORS.primary};
  background: #EDF2EB;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 4px;
`;

const QuoteItemDetail = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #5A6B75;
  flex-shrink: 0;
  white-space: nowrap;
`;

const QuoteTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const QuoteTotalLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
`;

const QuoteTotalValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.active};
`;

const AccountBar = styled.div`
  background: #F9F9F6;
  border-radius: 10px;
  padding: 14px 16px;
`;

const AccountLabel = styled.p`
  font-size: 11px;
  color: #8C9AA4;
  margin-bottom: 4px;
`;

const AccountValue = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #1E2A32;
`;

/* 이전 견적 (비활성) */
const PrevQuoteNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #8C9AA4;
  margin-bottom: 8px;
`;
const PrevQuoteBox = styled.div`
  background: #F4F3F8;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 6px;
  opacity: 0.6;
`;
const PrevQuoteLabel = styled.p`
  font-size: 13px;
  color: #8C9AA4;
  text-decoration: line-through;
`;

/* 견적서 전체화면 */
const QuotationOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.2s ease;
`;

const QuotationPage = styled.div`
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const QuotationCloseBtn = styled.button`
  position: absolute; top: 8px; right: 8px; z-index: 2;
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(0,0,0,0.5); color: #fff;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: rgba(0,0,0,0.7); }
`;

const QuotationDoc = styled.div`
  background: white;
  border-radius: 4px;
  padding: 32px 24px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
`;

const QDocTitle = styled.h2`
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #1E2A32;
  letter-spacing: 12px;
  margin-bottom: 4px;
`;

const QDocCompany = styled.p`
  text-align: center;
  font-size: 13px;
  color: #8C9AA4;
  margin-bottom: 24px;
`;

const QDocMeta = styled.div`
  margin-bottom: 20px;
  border-bottom: 2px solid ${COLORS.header};
  padding-bottom: 12px;
`;

const QDocMetaRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 4px;
`;

const QDocMetaLabel = styled.span`
  font-size: 12px;
  color: #8C9AA4;
  width: 60px;
`;

const QDocMetaVal = styled.span`
  font-size: 12px;
  color: #1E2A32;
  font-weight: 600;
`;

const QDocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
`;

const QDocThead = styled.thead`
  background: ${COLORS.header};
`;

const QDocTh = styled.th`
  padding: 8px 6px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  text-align: ${({ $left }) => $left ? 'left' : 'right'};
  white-space: nowrap;
`;

const QDocTd = styled.td`
  padding: 10px 6px;
  font-size: 12px;
  color: #1E2A32;
  text-align: ${({ $left }) => $left ? 'left' : 'right'};
  border-bottom: 1px solid #F0F0EE;
  white-space: ${({ $left }) => $left ? 'normal' : 'nowrap'};
  word-break: keep-all;
  line-height: 1.4;
`;
const QDocOptionTag = styled.span`
  display: inline-block;
  font-size: 10px;
  color: ${COLORS.primary};
  background: #EDF2EB;
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: 4px;
`;
const QDocSubtotalTd = styled.td`
  padding: 4px 6px 10px; font-size: 11px; color: #8C9AA4;
  text-align: right; border-bottom: 1px solid #F0F0EE;
`;

const QDocTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 2px solid ${COLORS.header};
`;

const QDocTotalLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1E2A32;
`;

const QDocTotalValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.active};
`;

const QDocVatNote = styled.p`
  text-align: right;
  font-size: 12px;
  color: #E85830;
  font-weight: 500;
  margin-top: -4px;
  padding-bottom: 8px;
`;

const QDocFooter = styled.div`
  margin-top: 32px;
  text-align: right;
`;

const QDocNote = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  text-align: center;
  margin-bottom: 24px;
`;

const QDocRecipient = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #1E2A32;
  margin-bottom: 16px;
`;

const QDocStampWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

const QDocStampLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1E2A32;
`;

const QDocStamp = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 3px solid #C0392B;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-15deg);
  opacity: 0.85;
`;

const StampInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
`;

const StampText = styled.span`
  font-size: 16px;
  font-weight: 900;
  color: #C0392B;
  letter-spacing: 2px;
`;
const QDocCompanyInfo = styled.div`
  margin-top: 24px; padding-top: 20px; border-top: 1px solid #E8E8E4;
  text-align: center;
`;
const QDocCompanyName = styled.div`font-size: 13px; color: #1E2A32; font-weight: 500; margin-bottom: 6px;`;
const QDocCompanyDetail = styled.div`font-size: 11px; color: #8C9AA4; line-height: 1.6;`;

/* 주문요청 배송지 바텀시트 */
const OrderBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 901;
  padding: 0 20px 34px;
  max-height: 85vh;
  overflow-y: auto;
  animation: ${slideUp} 0.25s ease;
`;

const OrderSheetBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 8px;
`;

const AddrSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AddrSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
`;

const OrderSummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #F9F9F6;
  border-radius: 10px;
  padding: 12px 14px;
`;

const OrderSummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #5A6B75;
`;

const EmptyAddrBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  background: #F9F9F6;
  border-radius: 10px;
`;

const EmptyAddrText = styled.p`
  font-size: 13px;
  color: #8C9AA4;
`;

const AddAddrBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: 1px dashed #D0D0D0;
  background: white;
  color: #5A6B75;
  font-size: 13px;
  font-weight: 500;
  &:active { background: #F9F9F6; }
`;

const AddrCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  border-radius: 10px;
  border: 1.5px solid ${({ $selected }) => $selected ? COLORS.primary : '#EEEEED'};
  background: ${({ $selected }) => $selected ? '#F5F9F4' : 'white'};
  cursor: pointer;
  transition: all 0.15s;
`;

const AddrRadio = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${({ $selected }) => $selected ? COLORS.primary : '#D0D0D0'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const AddrCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const AddrCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`;

const AddrLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
`;

const DefaultBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: ${COLORS.primary};
  background: #E8F0E6;
  padding: 2px 6px;
  border-radius: 4px;
`;

const AddrName = styled.p`
  font-size: 12px;
  color: #5A6B75;
  margin-bottom: 2px;
`;

const AddrText = styled.p`
  font-size: 12px;
  color: #8C9AA4;
  word-break: keep-all;
`;

const AddrDeliveryTag = styled.span`
  display: inline-block;
  font-size: 11px;
  color: #4A6741;
  background: #EDF2EB;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 4px;
`;

const AddrBtnGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  align-self: center;
`;

const AddrEditBtn = styled.button`
  flex-shrink: 0;
  font-size: 12px;
  color: #8C9AA4;
  padding: 4px 10px;
  border-radius: 6px;
  background: #F4F3F8;
  &:active { background: #E8E7ED; }
`;

const AddrDeleteBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #D32F2F;
  padding: 4px 10px;
  border-radius: 6px;
  background: #FFF0F0;
  &:active { background: #FFDDDD; }
`;

/* 계산서 발행 */
const InvoiceCheckRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 4px;
`;

const InvoiceOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  border: 1.5px solid ${({ $active }) => $active ? COLORS.primary : '#EEEEED'};
  background: ${({ $active }) => $active ? '#F5F9F4' : 'white'};
  font-size: 14px;
  color: ${({ $active }) => $active ? COLORS.primary : '#8C9AA4'};
  font-weight: ${({ $active }) => $active ? 600 : 400};
  cursor: pointer;
  transition: all 0.15s;
`;

const InvoiceRadio = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ $active }) => $active ? COLORS.primary : '#CCC'};
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $active }) => $active ? COLORS.primary : 'transparent'};
  }
`;

/* 배송지 입력 폼 */
const AddrFormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddrFormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AddrFormLabel = styled.label`
  font-size: 12px;
  color: #5A6B75;
  font-weight: 500;
`;
const RequiredTag = styled.span`
  color: #E85830;
  font-size: 11px;
  font-weight: 400;
`;
const addrToastSlideUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
`;
const AddrToast = styled.div`
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 12px;
  padding: 14px 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  z-index: 9999;
  min-width: 270px;
  max-width: 340px;
  animation: ${addrToastSlideUp} 0.3s ease;
`;
const AddrToastText = styled.div`
  flex: 1;
`;
const AddrToastLine1 = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #1E2A32;
  line-height: 1.4;
`;
const AddrToastLine2 = styled.p`
  font-size: 13px;
  color: #5A6B75;
  margin-top: 2px;
`;

const AddrInput = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #E0E0E0;
  font-size: 14px;
  color: #1E2A32;
  outline: none;
  min-width: 0;
  &:focus { border-color: ${COLORS.primary}; }
  &::placeholder { color: #C0C0C0; }
`;

const AddrSearchRow = styled.div`
  display: flex;
  gap: 8px;
`;

const AddrSearchBtn = styled.button`
  width: 44px;
  height: 42px;
  border-radius: 8px;
  background: ${COLORS.header};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:active { opacity: 0.85; }
  &:disabled { opacity: 0.4; pointer-events: none; }
`;

const DeliveryMethodRow = styled.div`
  display: flex;
  gap: 8px;
`;

const DeliveryMethodBtn = styled.button`
  flex: 1;
  padding: 10px 0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  border: 1.5px solid ${p => p.$active ? '#4A6741' : '#E0E0E0'};
  background: ${p => p.$active ? '#EDF2EB' : '#fff'};
  color: ${p => p.$active ? '#4A6741' : '#888'};
  cursor: pointer;
`;

const DefaultCheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
`;

const DefaultCheckLabel = styled.span`
  font-size: 13px;
  color: #5A6B75;
`;

const AddrFormBtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const OrderSubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  background: ${({ disabled }) => disabled ? '#D0D0D0' : COLORS.header};
  color: white;
  font-size: 15px;
  font-weight: 600;
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
  margin-top: 4px;
  &:active { opacity: 0.85; }
`;

const AddrOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const AddrOverlayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #E5E7EB;
`;

const AddrOverlayTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #1E2A32;
`;

const AddrOverlayClose = styled.button`
  font-size: 14px;
  color: #5A6B75;
`;

const AddrEmbed = styled.div`
  flex: 1;
`;

